import { useApi, api } from '../api/client';
import { Card, CardHead, Kpi, CountUp, FunnelChart, DeptBreakdown, Loading, ErrorBox, Reveal } from '../components/UI';
import { feishuNeeds } from '../data/feishuData';
import { DASHBOARD_MODULES } from '../config';

export default function Dashboard({ onOpenModule }: { onOpenModule: (m: string) => void }) {
  const { data, loading, error } = useApi(() => api.dashboard(), []);

  if (loading) return <Loading />;
  if (error || !data) return <ErrorBox message={error || 'No data'} />;

  const { hero, kpi, funnel, activity } = data;
  // Probation data derived from Feishu needs

  return (
    <div className="fade-in">
      <div className="scanline-overlay" />
      <div className="dashboard-hero">
        <div className="sweep-line" />
        <div className="binary-noise">0 1 1 0 1 0 1 1 0 0 1 0 0 1 1 0 0 1 0 1 1 0 1 0 0 1 0 1 0 1 1 0 0 1 0 0 1 1 0 0 1 0 1 1 0 1 0 0 1 0</div>
        <div className="log-stream">$ tail -f /var/log/recruit/sync.log
[02:14:01] indexed 1284 resumes
[02:14:02] digest pipeline OK
[02:14:05] queued 247 → 5 lanes
[02:14:11] semantic index refresh
[02:14:13] 9 offers dispatched</div>
        <div className="eyebrow"><span className="live-dot" /> ATS · 概览 <span className="ver">v0.3.7</span></div>
      <div className="hero-greeting">{hero.greeting}</div>
        <h1 className="hero-title">
          林婉清，<em>欢迎回来</em> · {hero.title}<span className="blink-cursor" />
        </h1>
        <div className="hero-meta stagger">
          <div className="hero-meta-item">
            <div className="hero-meta-num">{hero.stats.onboarding}</div>
            <div className="hero-meta-label">本月入职</div>
          </div>
          <div className="hero-meta-item">
            <div className="hero-meta-num">{hero.stats.needs}</div>
            <div className="hero-meta-label">招聘需求</div>
          </div>
          <div className="hero-meta-item">
            <div className="hero-meta-num">{hero.stats.resumes}</div>
            <div className="hero-meta-label">收到简历</div>
          </div>
          <div className="hero-meta-item">
            <div className="hero-meta-num">{hero.stats.offers}</div>
            <div className="hero-meta-label">已发 Offer</div>
          </div>
          <div className="hero-meta-item">
            <div className="hero-meta-num">
              {hero.stats.avgDays}<small style={{ fontSize: 14, opacity: 0.7 }}>天</small>
            </div>
            <div className="hero-meta-label">平均到岗周期</div>
          </div>
        </div>
      </div>

      <div className="grid grid-4 stagger" style={{ marginBottom: 24 }}>
        <Kpi label="在招 HC" value={<CountUp end={kpi.inProgress} />} unit="个" trend={{ type: 'up', text: '↑ 12% vs 上月' }} spark={[20,18,22,16,12,14,8,11,5,7,3]} />
        <Kpi label="本月面试" value={<CountUp end={kpi.interviews} />} unit="场" trend={{ type: 'up', text: '↑ 23% vs 上月' }} spark={[12,14,10,16,20,18,22,24,19,26,28]} />
        <Kpi label="人才库" value={<CountUp end={kpi.talentPool} />} unit="份" trend={{ type: 'up', text: '↑ 156 较上月' }} spark={[30,28,32,35,33,38,40,42,45,48,52]} />
        <Kpi label="试用期通过率" value={<CountUp end={kpi.passRate} />} unit="%" trend={{ type: 'flat', text: '→ 与上月持平' }} spark={[42,40,41,43,42,41,42,43,42,41,42]} />
      </div>

      <div className="grid grid-2 reveal" style={{ gridTemplateColumns: '2fr 1fr', marginBottom: 24 }}>
        <Card>
          <CardHead title="本月招聘漏斗">
            <a className="btn-text" onClick={() => onOpenModule('analytics')} style={{ cursor: 'pointer' }}>
              查看详情 →
            </a>
          </CardHead>
          <FunnelChart data={funnel} />
        </Card>

        <Card>
          <CardHead title="部门需求分布" sub={<span>{feishuNeeds.length} 项需求</span>} />
          <DeptBreakdown needs={feishuNeeds} />
        </Card>
      </div>

      <div className="grid grid-2" style={{ gridTemplateColumns: '2fr 1fr', marginBottom: 24 }}>
        <Card>
          <CardHead title="实时动态" sub={<span><span className="live-dot" /> 系统在线</span>} />
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            {activity.map((a) => (
              <div key={a.id} className="activity-item" style={{ display: 'flex', gap: 12, alignItems: 'flex-start', padding: '8px 0', borderBottom: '1px solid var(--paper-deep)' }}>
                <div className="live-dot" style={{ marginTop: 4, flexShrink: 0 }} />
                <div style={{ flex: 1 }}>
                  <div className="text-sm">{a.text}</div>
                  <div className="text-xs text-ink-3 mono" style={{ marginTop: 2 }}>{a.time}</div>
                </div>
              </div>
            ))}
          </div>
        </Card>

        <Card>
          <CardHead title="系统状态" />
          <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <span className="text-sm text-ink-3">简历索引</span>
              <span className="text-sm" style={{ color: 'var(--bamboo)' }}><span className="live-dot" /> 正常</span>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <span className="text-sm text-ink-3">AI 初筛引擎</span>
              <span className="text-sm" style={{ color: 'var(--bamboo)' }}><span className="live-dot" /> 运行中</span>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <span className="text-sm text-ink-3">面试调度</span>
              <span className="text-sm" style={{ color: 'var(--gold)' }}>排队中</span>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <span className="text-sm text-ink-3">背调服务</span>
              <span className="text-sm" style={{ color: 'var(--bamboo)' }}><span className="live-dot" /> 正常</span>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <span className="text-sm text-ink-3">D1 数据库</span>
              <span className="text-sm text-ink-3">同步中</span>
            </div>
          </div>
        </Card>
      </div>

      <Reveal>
        <div className="module-banner">
          <h3>招聘全流程 · 8 大核心模块</h3>
          <span className="stage-tag">精简版总览</span>
        </div>

        <div className="mod-cards">
        {DASHBOARD_MODULES.map((m) => (
          <div key={m.module} className="mod-card corner-frame" onClick={() => onOpenModule(m.module)}>
            <div className="mod-num">{m.num}</div>
            <div className="mod-icon">{m.icon}</div>
            <div className="mod-name">{m.name}</div>
            <div className="mod-desc">{m.desc}</div>
            <div className="mod-meta">
              <span className="mod-meta-count">{m.count}</span>
              <span className="mod-meta-stage">{m.stage}</span>
            </div>
          </div>
        ))}
        </div>
      </Reveal>
    </div>
  );
}
